import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth, hasPerm } from "../context/AuthContext";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { X, Plus, Search } from "lucide-react";
import { toast } from "sonner";

// Sélecteur de tags libres (ex. "S26 Ultra" sur une coque compatible) — sous
// la catégorie/sous-catégorie de l'article. On tape dans la barre de
// recherche : si un tag correspondant existe déjà, Entrée ou un clic dessus
// l'ajoute directement ; sinon, si l'utilisateur a la permission
// "stock.tags_create", un bouton "Créer le tag" apparaît pour le créer et
// l'ajouter en un geste.
export function TagPicker({ value, onChange, disabled }) {
  const { user } = useAuth();
  const tags = value || [];
  const [query, setQuery] = useState("");
  const [resultats, setResultats] = useState([]);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const peutCreer = hasPerm(user, "stock", "tags_create");

  useEffect(() => {
    if (!query.trim()) { setResultats([]); return; }
    const timeout = setTimeout(async () => {
      try {
        const { data } = await api.get("/tags", { params: { q: query.trim() } });
        setResultats(data.filter((t) => !tags.includes(t.nom)));
      } catch (e) {
        // Recherche silencieuse : une erreur ici ne doit pas bloquer la saisie.
      }
    }, 200);
    return () => clearTimeout(timeout);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const correspondanceExacte = resultats.some((t) => t.nom.toLowerCase() === query.trim().toLowerCase());

  const ajouterTag = (nom) => {
    if (tags.includes(nom)) { setQuery(""); setOpen(false); return; }
    onChange([...tags, nom]);
    setQuery("");
    setResultats([]);
    setOpen(false);
  };

  const retirerTag = (nom) => {
    onChange(tags.filter((t) => t !== nom));
  };

  const creerEtAjouter = async () => {
    const nom = query.trim();
    if (!nom) return;
    setCreating(true);
    try {
      const { data } = await api.post("/tags", { nom });
      ajouterTag(data.nom);
      toast.success(`Tag « ${data.nom} » créé et ajouté`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (correspondanceExacte) {
        const exact = resultats.find((t) => t.nom.toLowerCase() === query.trim().toLowerCase());
        ajouterTag(exact.nom);
      } else if (resultats.length > 0) {
        ajouterTag(resultats[0].nom);
      } else if (peutCreer && query.trim()) {
        creerEtAjouter();
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="space-y-1.5">
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="tag-picker-selected">
          {tags.map((nom) => (
            <Badge key={nom} variant="secondary" className="gap-1 pr-1" data-testid={`tag-picker-chip-${nom}`}>
              {nom}
              {!disabled && (
                <button type="button" onClick={() => retirerTag(nom)} className="hover:text-destructive" data-testid={`tag-picker-remove-${nom}`}>
                  <X size={11} />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
      {!disabled && (
        <div className="relative">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Rechercher ou ajouter un tag (ex. S26 Ultra)..."
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onKeyDown={handleKeyDown}
              className="pl-8 h-9 text-sm"
              data-testid="tag-picker-search"
            />
          </div>
          {open && query.trim() && (
            <div className="absolute z-10 top-full mt-1 left-0 right-0 rounded-lg border border-border bg-popover shadow-lg overflow-hidden" data-testid="tag-picker-results">
              {resultats.map((t) => (
                <button
                  key={t.id} type="button" onClick={() => ajouterTag(t.nom)}
                  className="w-full text-left text-sm px-3 py-2 hover:bg-secondary"
                  data-testid={`tag-picker-option-${t.id}`}
                >
                  {t.nom}
                </button>
              ))}
              {!correspondanceExacte && (
                peutCreer ? (
                  <Button
                    type="button" variant="ghost" size="sm"
                    className="w-full justify-start gap-1.5 rounded-none text-primary"
                    onClick={creerEtAjouter} disabled={creating}
                    data-testid="tag-picker-create"
                  >
                    <Plus size={13} /> {creating ? "Création..." : `Créer le tag « ${query.trim()} »`}
                  </Button>
                ) : resultats.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-3 py-2" data-testid="tag-picker-no-permission">
                    Aucun tag trouvé — vous n'avez pas la permission d'en créer un nouveau.
                  </p>
                ) : null
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
